import { useEffect, useMemo, useState } from "react";
import { Star, X } from "lucide-react";
import dayjs from "@lib/dayjs";
import LoadingAnimation from "@assets/svg/LoadingAnimation";

const STAR_VALUES = [1, 2, 3, 4, 5];

const getPlacedLabel = (value?: string | Date) => {
  if (!value) return "N/A";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("hh:mm a DD MMM YY") : "N/A";
};

const ReferralRatingModal = ({
  open,
  jobApplication,
  onClose,
  onSubmit,
  isSubmitting = false,
}: {
  open: boolean;
  jobApplication: any | null;
  onClose: () => void;
  onSubmit: (payload: {
    matchId: string;
    rating: number;
    comment?: string;
  }) => Promise<void> | void;
  isSubmitting?: boolean;
}) => {
  const isRated = useMemo(
    () => Boolean(jobApplication?.candidateRating || jobApplication?.referredStatus === "rated"),
    [jobApplication],
  );
  const initialRating = Number(jobApplication?.candidateRating || 0);
  const [rating, setRating] = useState(initialRating);
  const [comment, setComment] = useState(jobApplication?.candidateRatingComment || "");

  useEffect(() => {
    if (!open) return;
    setRating(Number(jobApplication?.candidateRating || 0));
    setComment(jobApplication?.candidateRatingComment || "");
  }, [open, jobApplication]);

  if (!open || !jobApplication) return null;

  const ratingText =
    rating > 0 ? `${rating}/5` : "Tap a star to rate";

  const handleSubmit = async () => {
    if (isRated || !jobApplication?.matchId || rating < 1 || rating > 5) return;
    await onSubmit({
      matchId: jobApplication.matchId,
      rating,
      comment: comment?.trim() || undefined,
    });
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 px-4 py-6 backdrop-blur-sm">
      <div className="relative w-full max-w-4xl overflow-hidden rounded-[28px] bg-white shadow-[0_30px_80px_rgba(15,23,42,0.28)]">
        <div className="absolute inset-x-0 top-0 h-2 bg-gradient-to-r from-emerald-400 via-sky-400 to-blue-500" />
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 rounded-full bg-slate-100 p-2 text-slate-600 transition hover:bg-slate-200"
        >
          <X size={18} />
        </button>

        <div className="border-b border-slate-200 px-6 pb-6 pt-7 md:px-8">
          <div className="flex flex-wrap items-center gap-3">
            <span className="rounded-full bg-emerald-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-700">
              Referral Rating
            </span>
            {isRated ? (
              <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                Rated
              </span>
            ) : (
              <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">
                Action Required
              </span>
            )}
          </div>

          <div className="mt-4 flex flex-col gap-2">
            <h3 className="text-2xl font-bold text-slate-900">
              Rate the referral partner
            </h3>
            <p className="max-w-2xl text-sm leading-6 text-slate-600">
              Your placement was unlocked by a referral partner. Please help us
              capture how helpful the referral was by leaving a quick rating.
            </p>
          </div>
        </div>

        <div className="grid gap-6 px-6 py-6 md:px-8 lg:grid-cols-[1.05fr_0.95fr]">
          <div className="rounded-[24px] border border-slate-200 bg-slate-50/80 p-5">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Referral details
                </p>
                <h4 className="mt-2 text-xl font-semibold text-slate-900">
                  {jobApplication?.referredByReferralName ||
                    jobApplication?.referralName ||
                    "Referral partner"}
                </h4>
              </div>
              <div className="rounded-2xl bg-white px-4 py-3 text-right shadow-sm ring-1 ring-slate-200">
                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                  Match Ref
                </p>
                <p className="mt-1 text-sm font-semibold text-slate-900">
                  {jobApplication?.matchRefNo || "N/A"}
                </p>
              </div>
            </div>

            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                  Job Title
                </p>
                <p className="mt-2 text-sm font-semibold text-slate-900">
                  {jobApplication?.job?.title || "N/A"}
                </p>
                <p className="mt-1 text-xs text-slate-500">
                  {jobApplication?.job?.refNo || "N/A"}
                </p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                  Placement Time
                </p>
                <p className="mt-2 text-sm font-semibold text-slate-900">
                  {getPlacedLabel(jobApplication?.placedAt)}
                </p>
                <p className="mt-1 text-xs text-slate-500">
                  Rating unlocks only after placement
                </p>
              </div>
            </div>

            <div className="mt-5 rounded-[20px] border border-sky-100 bg-sky-50 px-4 py-4 text-sm leading-6 text-slate-700">
              If the referral partner helped you land this role, your feedback
              helps us measure how effective the referral network is for future
              candidates.
            </div>
          </div>

          <div className="rounded-[24px] border border-slate-200 bg-white p-5">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                  Your rating
                </p>
                <p className="mt-2 text-lg font-semibold text-slate-900">
                  How helpful was this referral?
                </p>
              </div>
              <div className="rounded-full bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-700">
                {ratingText}
              </div>
            </div>

            <div className="mt-6 flex items-center gap-2">
              {STAR_VALUES.map((value) => {
                const active = rating >= value;
                return (
                  <button
                    key={value}
                    type="button"
                    disabled={isRated}
                    onClick={() => setRating(value)}
                    className={`flex h-12 w-12 items-center justify-center rounded-2xl border transition ${
                      active
                        ? "border-amber-300 bg-amber-50 text-amber-500 shadow-sm"
                        : "border-slate-200 bg-slate-50 text-slate-300 hover:border-slate-300 hover:text-slate-500"
                    } ${isRated ? "cursor-not-allowed opacity-80" : ""}`}
                    aria-label={`Rate ${value} out of 5`}
                  >
                    <Star
                      size={20}
                      fill={active ? "currentColor" : "none"}
                      strokeWidth={active ? 1.5 : 1.8}
                    />
                  </button>
                );
              })}
            </div>

            <div className="mt-6">
              <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                Optional comment
              </label>
              <textarea
                value={comment}
                disabled={isRated}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Add a short note about how the referral helped you..."
                className="min-h-[140px] w-full resize-none rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-sky-400 focus:bg-white focus:ring-4 focus:ring-sky-100 disabled:cursor-not-allowed disabled:opacity-80"
              />
            </div>

            <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
              <div className="text-xs text-slate-500">
                Ratings are capped at 5 and stored on the referral record.
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-full border border-slate-200 px-5 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                >
                  {isRated ? "Close" : "Cancel"}
                </button>
                {!isRated ? (
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={isSubmitting || rating < 1}
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    {isSubmitting ? (
                      <>
                        <LoadingAnimation color="white" className="h-4 w-4" />
                        Submitting
                      </>
                    ) : (
                      "Submit rating"
                    )}
                  </button>
                ) : (
                  <div className="rounded-full bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700">
                    Submitted rating: {rating || jobApplication?.candidateRating || 0}/5
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReferralRatingModal;
